import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth/AuthContext';

export interface UnreadMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_display_name: string;
  content: string;
  created_at: string;
  deal_title?: string;
}

export const useUnreadMessages = () => {
  const [unreadMessages, setUnreadMessages] = useState<UnreadMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  const fetchUnreadMessages = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      // Get conversations where user is a participant
      const { data: conversations, error: convError } = await supabase
        .from('community_conversations')
        .select('*')
        .or(`participant_1_id.eq.${currentUser.id},participant_2_id.eq.${currentUser.id}`)
        .order('last_message_at', { ascending: false });

      if (convError) {
        console.error('Error fetching conversations:', convError);
        return;
      }

      if (!conversations || conversations.length === 0) {
        setUnreadMessages([]);
        return;
      }

      // Get unread messages from these conversations
      const { data: messages, error: msgError } = await supabase
        .from('community_messages')
        .select(`
          *,
          community_conversations!inner(
            participant_1_id,
            participant_2_id,
            deal_listing_id,
            deal_exchange_listings(title)
          )
        `)
        .in('conversation_id', conversations.map(c => c.id))
        .neq('sender_id', currentUser.id)
        .is('read_at', null)
        .order('created_at', { ascending: false })
        .limit(10);

      if (msgError) {
        console.error('Error fetching messages:', msgError);
        return;
      }

      // Get sender profiles
      const senderIds = [...new Set(messages?.map(m => m.sender_id) || [])];
      const { data: profiles, error: profileError } = await supabase
        .from('community_profiles')
        .select('user_id, display_name')
        .in('user_id', senderIds);

      if (profileError) {
        console.error('Error fetching profiles:', profileError);
      }

      // Map messages with sender info
      const enrichedMessages: UnreadMessage[] = (messages || []).map(msg => ({
        id: msg.id,
        conversation_id: msg.conversation_id,
        sender_id: msg.sender_id,
        sender_display_name: profiles?.find(p => p.user_id === msg.sender_id)?.display_name || 'Unknown User',
        content: msg.content,
        created_at: msg.created_at,
        deal_title: (msg as any).community_conversations?.deal_exchange_listings?.title
      }));

      setUnreadMessages(enrichedMessages);
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markMessageAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('community_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('id', messageId);

      if (error) {
        console.error('Error marking message as read:', error);
        return;
      }

      // Remove from unread messages
      setUnreadMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const markConversationAsRead = async (conversationId: string) => {
    try {
      const { error } = await supabase
        .from('community_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .neq('sender_id', currentUser?.id)
        .is('read_at', null);

      if (error) {
        console.error('Error marking conversation as read:', error);
        return;
      }

      // Remove messages from this conversation
      setUnreadMessages(prev => prev.filter(msg => msg.conversation_id !== conversationId));
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    
    fetchUnreadMessages();

    // Create unique channel name for this user to avoid conflicts
    const channelName = `unread_messages_${currentUser.id}_${Date.now()}`;
    
    // Set up real-time subscription for new messages
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'community_messages',
          filter: `sender_id=neq.${currentUser.id}`
        },
        () => {
          fetchUnreadMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id]);

  return {
    unreadMessages,
    loading,
    markMessageAsRead,
    markConversationAsRead,
    refetch: fetchUnreadMessages
  };
};